/**
 * 9 - Photograph a Secret Experimental Aircraft (03 §2): get into the hangar and stay 20 s
 * uninterrupted. Being seen within 25 m of it spoils the job; the intended answer is a
 * uniform from the guard house nearby, or perfect timing between the sentries' sweeps.
 */

import { atan2 } from '../../sim/math';
import { NO_STAND } from '../builder';
import {
  checker, clutter, countKind, guardAtDoor, guardsAbout, insideRect, required, ruleOf, structure, uniformDonor,
} from './helpers';
import type { ArchetypeSpec } from './types';

/** Free nav cells wanted inside the dwell zone: the two aisles beside the aircraft give a couple of dozen. */
const STANDING_ROOM = 8;

export const photographAircraft: ArchetypeSpec = {
  id: 'photograph-aircraft',
  name: 'PHOTOGRAPH A SECRET AIRCRAFT',
  nativeBiome: 'arctic',
  baseThreat: 4,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [56, 64], d: [32, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const hangar = required(g, 'hangar', { target: true, margin: 2 });
    // The thing itself, nose to the doors. It is photographed, not destroyed, so it is no target; the shed
    // does not stop feet (props.ts), and the aisles either side of the wings are where the ranger stands.
    if (hangar.id >= 0) g.map.add('aircraft', hangar.x, hangar.z, { yaw: hangar.yaw });
    const at = [{ x: hangar.x, z: hangar.z }];
    const house = required(g, 'guardHouse', { minDist: 13, from: at, margin: 1.4 });
    // The taxiway: from the hangar doors toward the gate.
    g.map.road([[hangar.x, hangar.z + 5.5], [hangar.x, Math.min(g.inner.z1, hangar.z + 11)]], 6);
    structure(g, 'hut', { minDist: 12, from: at });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'barrel', 2);
    clutter(g, 'crate', 3);

    // The man whose uniform solves the mission stands at the guard house door.
    guardAtDoor(g, house);
    // Two sentries sweep round the hangar; the gap between them is the other way in.
    const b = g.map.bounds('hangar', hangar.x, hangar.z);
    const loop: Array<[number, number]> = [];
    const round = [[b.x0 - 4, b.z1 + 4], [b.x1 + 4, b.z1 + 4], [b.x1 + 4, b.z0 - 4], [b.x0 - 4, b.z0 - 4]] as const;
    for (const [x, z] of round) {
      // Stay inside the wire however close to it the hangar stands.
      const cx = Math.max(g.rect.x0 + 2.5, Math.min(g.rect.x1 - 2.5, x));
      const cz = Math.max(g.rect.z0 + 2.5, Math.min(g.rect.z1 - 2.5, z));
      const spot = g.map.nearestOpen(cx, cz, 5, NO_STAND);
      if (spot) loop.push(spot);
    }
    if (loop.length < 4) g.map.problems.push('photograph-aircraft: no room for the sentries to walk round the hangar');
    else {
      for (const start of [0, 2]) {
        const nodes = loop.map((_, i) => loop[(i + start) % 4] ?? ([0, 0] as [number, number]));
        const [a, n] = nodes;
        if (!a || !n) continue;
        const facing = atan2(n[0] - a[0], n[1] - a[1]);
        g.map.spawn({ kind: g.elite ? 'elite' : 'rifleman', x: a[0], z: a[1], facing, patrol: nodes });
      }
    }
    guardsAbout(g, g.rng.int(2));
    const zone = { x0: b.x0 + 0.5, z0: b.z0 + 0.5, x1: b.x1 - 0.5, z1: b.z1 - 0.5 };
    return {
      objective: {
        kind: 'dwellInZone', label: 'PHOTOGRAPH AIRCRAFT', targetPropIds: [hangar.id], minFraction: 1,
        zone, dwellSeconds: 20,
        rules: [
          {
            type: 'notSeenNear', propId: hangar.id, radius: 25,
            orders: 'DO NOT BE SEEN WITHIN 25 M OF THE HANGAR', reason: 'SEEN AT THE HANGAR',
          },
          { type: 'recall', mode: 'normal' },
        ],
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('photograph-aircraft');
    const hangar = def.props.find((p) => p.kind === 'hangar');
    const planes = def.props.filter((p) => p.kind === 'aircraft');
    const [plane] = planes;
    check(
      planes.length === 1 && plane !== undefined && hangar !== undefined
        && plane.x === hangar.x && plane.z === hangar.z && plane.yaw === hangar.yaw && plane.target !== true,
      'one aircraft stands in the middle of the hangar, and is not a target',
    );
    check(hangar !== undefined && def.objective.targetPropIds[0] === hangar.id, 'the hangar must be the target');
    check(
      def.objective.kind === 'dwellInZone' && def.objective.dwellSeconds === 20,
      'objective must be 20 s in the hangar',
    );
    const zone = def.objective.zone;
    check(
      zone !== undefined && hangar !== undefined && insideRect(zone, hangar.x, hangar.z),
      'the dwell zone must be the hangar',
    );
    const room = zone ? probe.standable(zone) : 0;
    check(room >= STANDING_ROOM, 'the ranger must have room to stand in the hangar');
    const seen = ruleOf(def, 'notSeenNear');
    check(
      seen !== undefined && seen.propId === hangar?.id && seen.radius === 25,
      'being seen within 25 m of the hangar must matter',
    );
    check(countKind(def, 'guardHouse') >= 1, 'needs a guard house: the uniform is the intended way in');
    check(uniformDonor(def, 'guardHouse'), 'a guard must stand at the guard house: his is the uniform to take');
    return out;
  },
};
